<template>
  <div class="q-pa-md relative-position" style="min-height: 220px;">
    <q-inner-loading :showing="loading && !results.length">
      <loading-spinner />
    </q-inner-loading>

    <div class="row items-center q-col-gutter-sm q-mb-sm">
      <div class="col-12 col-sm-auto">
        <q-select
          v-model="statusFilter"
          :options="baseStatusOptions(true)"
          outlined
          dense
          emit-value
          map-options
          options-dense
          behavior="menu"
          style="min-width: 160px;"
          @update:model-value="refresh"
        />
      </div>
      <div class="col">
        <q-input
          v-model="search"
          outlined
          dense
          clearable
          debounce="300"
          placeholder="Search appointments..."
          @update:model-value="refresh"
        >
          <template #prepend>
            <q-icon name="mdi-magnify" />
          </template>
        </q-input>
      </div>
      <div
        v-if="can(permissions.canEditAppointments)"
        class="col-12 col-sm-auto"
      >
        <q-btn
          color="blue-7"
          icon="mdi-plus"
          label="New Appointment"
          no-caps
          unelevated
          class="full-width"
          @click="$emit('newAppointment')"
        />
      </div>
    </div>

    <div class="row items-center q-gutter-sm q-mb-md">
      <q-btn-toggle
        v-model="timeFilter"
        :options="timeOptions"
        no-caps
        size="12px"
        @update:model-value="refresh"
      />
      <q-toggle
        v-model="scheduledOnly"
        label="Scheduled only"
        dense
        @update:model-value="refresh"
      />
    </div>

    <no-results
      v-if="!loading && results.length === 0"
      icon="mdi-calendar-blank"
      title="No appointments"
      :subtitle="hasFilters ? 'No appointments match your filters.' : 'Appointments for this client will appear here.'"
      class="full-width"
    />

    <q-list
      v-else
      bordered
      separator
      class="rounded-borders"
    >
      <appointment-item
        v-for="appointment in results"
        :key="'appointment-' + appointment.id"
        clickable
        with-color
        :appointment="appointment"
        @click="$emit('viewAppointment', appointment.id)"
      />
    </q-list>

    <div
      v-if="hasMore"
      class="text-center q-mt-md"
    >
      <q-btn
        flat
        color="blue-7"
        no-caps
        label="Load more"
        :loading="loading"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<script>
import searchMixin from 'components/app/calendar/partials/mixins/searchMixin'
import statusMixin from 'components/app/calendar/partials/mixins/statusMixin'

/*
  Lists a client's appointments with status / search / upcoming-past / scheduled
  filters and view + create actions. Reuses the calendar searchMixin (which posts
  v1/appointments/search and exposes results/total/loading) and AppointmentItem
  (global). Viewing is delegated to the client modal's embedded appointment
  dialog; creating navigates to the scheduler.
*/
export default {
  mixins: [searchMixin, statusMixin],
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  emits: ['newAppointment', 'viewAppointment'],
  data () {
    return {
      statusFilter: null,
      search: '',
      timeFilter: 'all',
      scheduledOnly: false,
      rows: 20,
      timeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Upcoming', value: 'upcoming' },
        { label: 'Past', value: 'past' }
      ]
    }
  },
  computed: {
    hasMore () {
      return this.results.length < this.total
    },
    hasFilters () {
      return !!this.statusFilter || !!this.search || this.timeFilter !== 'all' || this.scheduledOnly
    },
    dateBounds () {
      const today = this.luxon.now().startOf('day').toISODate()
      if (this.timeFilter === 'upcoming') {
        return { start: today, end: null }
      }
      if (this.timeFilter === 'past') {
        return { start: null, end: today }
      }
      return { start: null, end: null }
    }
  },
  methods: {
    runSearch () {
      const bounds = this.dateBounds
      this.doSearch({
        clients: [this.client.id],
        filter: this.search,
        status: this.statusFilter,
        isScheduled: this.scheduledOnly,
        start: bounds.start,
        end: bounds.end,
        page: 1,
        rows: this.rows,
        sortBy: 'closest',
        sortDirection: 'asc'
      })
    },
    refresh () {
      this.rows = 20
      this.runSearch()
    },
    loadMore () {
      this.rows += 20
      this.runSearch()
    }
  },
  mounted () {
    this.runSearch()
    this.$bus.on('calendar:update', this.refresh)
    this.$bus.on('calendar:status', this.refresh)
    this.$bus.on('calendar:remove', this.refresh)
  },
  beforeUnmount () {
    this.$bus.off('calendar:update', this.refresh)
    this.$bus.off('calendar:status', this.refresh)
    this.$bus.off('calendar:remove', this.refresh)
  }
}
</script>
